import type {
	AgentView,
	MessageKind,
	MessageView,
	Priority,
	ServerToWeb,
} from "@walkie/protocol"
import { api, errorText } from "../api/client"
import type { ProjectSummary, ResolveInput, Room } from "../api/types"
import { isWithdrawn, oneLine, type MessageX } from "../lib/messages"
import {
	getState,
	loadHideGithub,
	loadPrefs,
	savePrefs,
	saveHideGithub,
	setState,
	setUi,
	type State,
} from "./store"

// --- toasts -----------------------------------------------------------------

let toastId = 0
export function toast(text: string, kind: "error" | "info" = "error") {
	const id = ++toastId
	setState(s => ({ toasts: [...s.toasts.slice(-3), { id, text, kind }] }))
	setTimeout(
		() => setState(s => ({ toasts: s.toasts.filter(t => t.id !== id) })),
		kind === "error" ? 6000 : 3500,
	)
}

async function attempt<T>(p: Promise<T>, what: string): Promise<T | null> {
	try {
		return await p
	} catch (e) {
		toast(`${what}: ${errorText(e)}`)
		return null
	}
}

// --- me & projects ------------------------------------------------------------

export async function loadMe() {
	try {
		const me = await api.me()
		setState({ me, meError: null })
		await route()
		return me
	} catch (e) {
		setState({ meError: errorText(e) })
		return null
	}
}

export function allProjects(s: State = getState()): ProjectSummary[] {
	return s.me?.teams.flatMap(t => t.projects) ?? []
}

export function currentProject(s: State = getState()): ProjectSummary | null {
	return allProjects(s).find(p => p.id === s.projectId) ?? null
}

// --- routing: "/" is the projects page, "/p/<id>" a project -----------------------------

const projectInPath = () => location.pathname.match(/^\/p\/([0-9a-f-]{36})\/?$/i)?.[1] ?? null

/** Shows whatever the URL names (on load, after loadMe, and on back/forward). */
export async function route() {
	const pid = projectInPath()
	const s = getState()
	if (pid && allProjects(s).some(p => p.id === pid)) {
		if (pid !== s.projectId) await openProject(pid, false)
		return
	}
	// A project you can't see (or none): the projects page.
	if (pid) history.replaceState(null, "", "/")
	if (s.projectId) goHome(false)
}

/** Leaves the open project for the projects page. */
export function goHome(push = true) {
	if (push && location.pathname !== "/") history.pushState(null, "", "/")
	setState({
		projectId: null,
		role: null,
		agents: {},
		people: [],
		rooms: {},
		messages: {},
		sending: {},
		threadsLoaded: {},
		metrics: null,
		loading: false,
		loadError: null,
	})
	setUi({
		roomId: null,
		threadId: null,
		selectedId: null,
		connect: false,
		settings: false,
		resolveFor: null,
		withdrawFor: null,
	})
}

export async function openProject(pid: string, push = true) {
	if (push && location.pathname !== `/p/${pid}`) history.pushState(null, "", `/p/${pid}`)
	savePrefs({ projectId: pid })
	setState(s => ({
		projectId: pid,
		role: null,
		agents: {},
		people: [],
		rooms: {},
		messages: {},
		sending: {},
		threadsLoaded: {},
		metrics: null,
		hasOlder: true,
		loading: true,
		loadError: null,
		hideGithub: loadHideGithub(pid),
		activity: { ...s.activity, [pid]: 0 },
	}))
	setUi({
		roomId: null,
		threadId: null,
		selectedId: null,
		authorFilter: null,
		filter: "all",
		resolveFor: null,
		withdrawFor: null,
		citeAdd: null,
	})
	await loadSnapshot(pid)
	void refreshMetrics()
}

export async function loadSnapshot(pid = getState().projectId) {
	if (!pid) return
	try {
		const snap = await api.snapshot(pid)
		if (getState().projectId !== pid) return
		const messages: Record<string, MessageView> = {}
		for (const m of snap.messages) messages[m.id] = m
		// keep anything we already had that the snapshot window no longer covers (older pages, threads)
		const prev = getState().messages
		for (const [id, m] of Object.entries(prev))
			if (!messages[id] && !id.startsWith("local:")) messages[id] = m
		for (const [id, m] of Object.entries(prev))
			if (id.startsWith("local:")) messages[id] = m
		const rooms: Record<string, Room> = {}
		for (const r of snap.rooms) rooms[r.id] = r
		const agents: Record<string, AgentView> = {}
		for (const a of snap.agents) agents[a.id] = a
		setState({
			role: snap.role,
			agents,
			people: snap.people,
			rooms,
			messages,
			loading: false,
			loadError: null,
		})
		const ui = getState().ui
		if (!ui.roomId || !rooms[ui.roomId]) {
			const saved = loadPrefs().rooms?.[pid]
			const room =
				(saved && rooms[saved]) ||
				snap.rooms.find(r => r.name === "general") ||
				snap.rooms.find(r => r.kind === "channel") ||
				snap.rooms[0]
			if (room) selectRoom(room.id)
		}
		if (!snap.agents.length) setUi({ connect: true })
	} catch (e) {
		setState({ loading: false, loadError: errorText(e) })
	}
}

export async function refreshMetrics() {
	const pid = getState().projectId
	if (!pid) return
	try {
		const metrics = await api.metrics(pid)
		if (getState().projectId !== pid) return
		setState(s => {
			const agents = { ...s.agents }
			for (const [id, st] of Object.entries(metrics.agents ?? {})) {
				const a = agents[id]
				if (a) agents[id] = { ...a, stats: st }
			}
			return { metrics, agents }
		})
	} catch {
		/* counters are best-effort */
	}
}

export async function loadOlder() {
	const s = getState()
	if (!s.projectId) return
	const seqs = Object.values(s.messages)
		.filter(m => !m.id.startsWith("local:"))
		.map(m => m.seq)
	if (!seqs.length) return
	const before = Math.min(...seqs)
	const r = await attempt(api.older(s.projectId, before), "Could not load older messages")
	if (!r) return
	upsertMessages(r.messages)
	if (r.messages.length < 200) setState({ hasOlder: false })
}

// --- rooms ----------------------------------------------------------------------

export function selectRoom(roomId: string) {
	const pid = getState().projectId
	if (pid) savePrefs({ rooms: { ...(loadPrefs().rooms ?? {}), [pid]: roomId } })
	setUi({ roomId, threadId: null, selectedId: null, resolveFor: null })
	markRead(roomId)
}

let readTimer: ReturnType<typeof setTimeout> | null = null
export function markRead(roomId: string) {
	const s = getState()
	const room = s.rooms[roomId]
	if (!room || !s.projectId) return
	if (room.readSeq >= room.lastSeq) return
	const seq = room.lastSeq
	setState(st => ({ rooms: { ...st.rooms, [roomId]: { ...room, readSeq: seq } } }))
	if (readTimer) clearTimeout(readTimer)
	const pid = s.projectId
	readTimer = setTimeout(() => void api.markRead(pid, roomId, seq).catch(() => {}), 500)
}

export async function createRoom(name: string) {
	const pid = getState().projectId
	if (!pid) return
	const clean = name
		.trim()
		.toLowerCase()
		.replace(/^#/, "")
		.replace(/[^a-z0-9-]+/g, "-")
	const r = await attempt(api.createRoom(pid, clean), "Could not create room")
	if (!r) return
	setState(s => ({
		rooms: {
			...s.rooms,
			[r.roomId]: s.rooms[r.roomId] ?? {
				id: r.roomId,
				projectId: pid,
				kind: "channel",
				name: clean,
				topic: null,
				lastSeq: 0,
				readSeq: 0,
			},
		},
	}))
	selectRoom(r.roomId)
}

export async function openDm(agentIds: string[], userIds: string[]) {
	const pid = getState().projectId
	if (!pid) return
	const r = await attempt(api.createDm(pid, agentIds, userIds), "Could not open DM")
	if (!r) return
	if (!getState().rooms[r.roomId]) await loadSnapshot(pid)
	selectRoom(r.roomId)
}

// --- threads & selection ----------------------------------------------------------

export async function openThread(rootId: string, focus = false) {
	const s = getState()
	const m = s.messages[rootId]
	const root = m?.threadId ?? rootId
	setUi({
		threadId: root,
		...(focus ? { focusComposer: { target: "thread", tick: Date.now() } } : {}),
	})
	if (!s.projectId || s.threadsLoaded[root] || root.startsWith("local:")) return
	const r = await attempt(api.thread(s.projectId, root), "Could not load thread")
	if (!r) return
	upsertMessages(r.messages)
	setState(st => ({ threadsLoaded: { ...st.threadsLoaded, [root]: true } }))
}

export function jumpToMessage(m: MessageView) {
	const s = getState()
	if (!s.messages[m.id]) upsertMessages([m])
	if (s.ui.roomId !== m.roomId) selectRoom(m.roomId)
	if (m.threadId) {
		setUi({ selectedId: m.threadId })
		void openThread(m.threadId)
	} else setUi({ selectedId: m.id })
	requestAnimationFrame(() =>
		document
			.getElementById(`m-${m.id}`)
			?.scrollIntoView({ block: "center", behavior: "smooth" }),
	)
}

export function focusAgent(agentId: string) {
	setUi({ rightOpen: true, focusAgentId: agentId })
	savePrefs({ rightOpen: true })
	requestAnimationFrame(() => {
		const el = document.getElementById(`agent-${agentId}`)
		el?.scrollIntoView({ block: "nearest", behavior: "smooth" })
		el?.focus({ preventScroll: true })
	})
	setTimeout(() => {
		if (getState().ui.focusAgentId === agentId) setUi({ focusAgentId: null })
	}, 2400)
}

// --- messages -----------------------------------------------------------------------

function upsertMessages(list: MessageView[]) {
	if (!list.length) return
	setState(s => {
		const messages = { ...s.messages }
		for (const m of list) messages[m.id] = m
		return { messages }
	})
}

export interface Draft {
	roomId: string
	replyTo?: string
	kind: MessageKind
	title?: string
	body: string
	to: string[]
	priority: Priority
	resolve?: { resolution?: string }
	/** Full message ids this one builds on (max 10). */
	cites?: string[]
	/** A decision this one replaces. */
	supersedes?: string
}

let localSeq = 0
export async function sendMessage(d: Draft) {
	const s = getState()
	if (!s.projectId || !s.me) return
	const clientMsgId = crypto.randomUUID()
	const localId = `local:${clientMsgId}`
	const payload = {
		roomId: d.roomId,
		replyTo: d.replyTo,
		kind: d.kind,
		title: d.title || undefined,
		body: d.body,
		to: d.to,
		priority: d.priority,
		clientMsgId,
		resolve: d.resolve ? { resolution: d.resolve.resolution, evidence: [] } : undefined,
		cites: d.cites?.length ? d.cites.slice(0, 10) : undefined,
		supersedes: d.supersedes || undefined,
	}
	const local: MessageView = {
		id: localId,
		projectId: s.projectId,
		roomId: d.roomId,
		seq: Number.MAX_SAFE_INTEGER - 100_000 + localSeq++,
		threadId: d.replyTo ? (s.messages[d.replyTo]?.threadId ?? d.replyTo) : null,
		kind: d.replyTo && d.kind === "message" ? "reply" : d.kind,
		title: d.title ?? null,
		body: d.body,
		author: { type: "user", id: s.me.user.id, handle: s.me.user.handle },
		toAgents: [],
		toUsers: [],
		priority: d.priority,
		quiet: false,
		refs: [],
		cites: d.cites ?? [],
		supersedes: d.supersedes ?? null,
		supersededBy: null,
		hop: 0,
		replyCount: 0,
		lastReplyAt: null,
		createdAt: new Date().toISOString(),
		request: null,
		deliveries: [],
	}
	setState(st => ({
		messages: { ...st.messages, [localId]: local },
		sending: { ...st.sending, [localId]: { status: "sending", error: null, payload } },
	}))
	await deliver(localId)
}

async function deliver(localId: string) {
	const s = getState()
	const job = s.sending[localId]
	if (!job || !s.projectId) return
	setState(st => ({
		sending: { ...st.sending, [localId]: { ...job, status: "sending", error: null } },
	}))
	try {
		const r = await api.post(s.projectId, job.payload as never)
		setState(st => {
			const messages = { ...st.messages }
			delete messages[localId]
			messages[r.message.id] = mergeIncoming(st.messages[r.message.id], r.message)
			const sending = { ...st.sending }
			delete sending[localId]
			return { messages, sending, ...bumpRoom(st, r.message) }
		})
		markRead(r.message.roomId)
	} catch (e) {
		setState(st => ({
			sending: {
				...st.sending,
				[localId]: { ...job, status: "failed", error: errorText(e) },
			},
		}))
	}
}

export const retrySend = (localId: string) => deliver(localId)
export function discardSend(localId: string) {
	setState(st => {
		const messages = { ...st.messages }
		delete messages[localId]
		const sending = { ...st.sending }
		delete sending[localId]
		return { messages, sending }
	})
}

/** Keep the richer copy: a frame without deliveries must not wipe ones we have. */
function mergeIncoming(prev: MessageView | undefined, next: MessageView): MessageView {
	if (!prev) return next
	return { ...next, deliveries: next.deliveries ?? prev.deliveries }
}

function bumpRoom(s: State, m: MessageView): Partial<State> {
	const room = s.rooms[m.roomId]
	if (!room || room.lastSeq >= m.seq) return {}
	return { rooms: { ...s.rooms, [m.roomId]: { ...room, lastSeq: m.seq } } }
}

// --- requests -------------------------------------------------------------------------

export async function claim(mid: string) {
	const pid = getState().projectId
	if (!pid) return
	const r = await attempt(api.claim(pid, mid), "Could not claim")
	if (r) upsertMessages([mergeIncoming(getState().messages[mid], r.message)])
}
export async function release(mid: string) {
	const pid = getState().projectId
	if (!pid) return
	const r = await attempt(api.release(pid, mid), "Could not release")
	if (r) upsertMessages([mergeIncoming(getState().messages[mid], r.message)])
}
export async function resolveRequest(mid: string, input: ResolveInput) {
	const pid = getState().projectId
	if (!pid) return false
	const r = await attempt(api.resolve(pid, mid, input), "Could not resolve")
	if (r) upsertMessages([mergeIncoming(getState().messages[mid], r.message)])
	return !!r
}

/** Claim as the signed-in person, taking it over from a claimer that is gone. */
export async function claimForMe(mid: string) {
	const m = getState().messages[mid]
	if (m?.request?.status === "claimed") {
		const pid = getState().projectId
		if (!pid) return
		const r = await attempt(api.release(pid, mid), "Could not release the old claim")
		if (!r) return
		upsertMessages([mergeIncoming(getState().messages[mid], r.message)])
	}
	await claim(mid)
}

/**
 * Hand an orphaned request to someone else. There is no reassign endpoint, so:
 * release any stale claim, then post a handoff reply to the new owner that
 * cites the request. The thread keeps the history; the new owner is woken.
 */
export async function reassignRequest(mid: string, handle: string, fromHandle?: string) {
	const s = getState()
	const m = s.messages[mid]
	if (!m || !s.projectId) return false
	if (m.request?.status === "claimed") {
		const r = await attempt(api.release(s.projectId, mid), "Could not release the old claim")
		if (!r) return false
		upsertMessages([mergeIncoming(getState().messages[mid], r.message)])
	}
	const h = handle.replace(/^@/, "")
	await sendMessage({
		roomId: m.roomId,
		replyTo: m.threadId ?? m.id,
		kind: "handoff",
		title: `Reassigned: ${oneLine(m, 160)}`,
		body:
			`reassigned to @${h}` +
			// No @ on the old owner: it is gone, and a mention would address it again.
			(fromHandle ? ` (previous owner ${fromHandle} is gone)` : "") +
			`. Original ${m.kind}: ${oneLine(m, 300)}`,
		to: [h],
		priority: m.priority,
		cites: [m.id],
	})
	toast(`Reassigned to @${h}`, "info")
	return true
}

// --- withdraw & cite ---------------------------------------------------------------------

/** Author of the message, or a project admin; never a pending local copy. */
export function canWithdraw(m: MessageView | undefined, s: State = getState()): boolean {
	if (!m || m.id.startsWith("local:") || isWithdrawn(m) || s.role === "viewer") return false
	if (s.role === "admin") return true
	return m.author.type === "user" && !!s.me && m.author.id === s.me.user.id
}

export async function withdrawMessage(mid: string) {
	const pid = getState().projectId
	if (!pid) return false
	let r: Awaited<ReturnType<typeof api.withdraw>>
	try {
		r = await api.withdraw(pid, mid)
	} catch (e) {
		toast(`Could not withdraw: ${errorText(e)}`)
		return false
	}
	const prev = getState().messages[mid]
	if (r && r.message) upsertMessages([mergeIncoming(prev, r.message)])
	else if (prev) {
		// The frame will carry the server's copy; show the tombstone now.
		const tomb: MessageX = {
			...prev,
			title: null,
			body: "",
			refs: [],
			withdrawn: true,
			withdrawnAt: new Date().toISOString(),
			withdrawnBy: getState().me?.user.handle ?? null,
			request:
				prev.request &&
				(prev.request.status === "open" || prev.request.status === "claimed")
					? { ...prev.request, status: "cancelled" }
					: prev.request,
		}
		upsertMessages([tomb])
	}
	toast("Message withdrawn", "info")
	return true
}

/** Add a message to the cites of the composer that fits it (thread if open there). */
export function citeInComposer(mid: string) {
	const s = getState()
	const m = s.messages[mid]
	if (!m) return
	const root = m.threadId ?? m.id
	const target = s.ui.threadId && s.ui.threadId === root ? "thread" : "main"
	setUi({
		citeAdd: { id: mid, target, tick: Date.now() },
		focusComposer: { target, tick: Date.now() },
	})
}

// --- agents -----------------------------------------------------------------------------

function upsertAgent(a: AgentView) {
	setState(s => ({
		agents: { ...s.agents, [a.id]: { ...a, stats: a.stats ?? s.agents[a.id]?.stats } },
	}))
}

export async function renameAgent(aid: string, handle: string) {
	const pid = getState().projectId
	if (!pid) return false
	const r = await attempt(api.patchAgent(pid, aid, { handle }), "Could not rename")
	if (r) upsertAgent(r.agent)
	return !!r
}

export async function setShared(aid: string, shared: boolean) {
	const pid = getState().projectId
	if (!pid) return
	const prev = getState().agents[aid]
	if (prev) upsertAgent({ ...prev, shared })
	const r = await attempt(
		api.patchAgent(pid, aid, { shared }),
		"Could not change participation",
	)
	if (r) upsertAgent(r.agent)
	else if (prev) upsertAgent(prev)
}

export async function setAutoResume(aid: string, autoResume: boolean) {
	const pid = getState().projectId
	if (!pid) return
	const prev = getState().agents[aid]
	if (prev) upsertAgent({ ...prev, autoResume })
	const r = await attempt(
		api.patchAgent(pid, aid, { autoResume }),
		"Could not change auto-resume",
	)
	if (r) upsertAgent(r.agent)
	else if (prev) upsertAgent(prev)
}

// --- realtime ---------------------------------------------------------------------------

export function applyFrame(f: ServerToWeb & { projectId?: string }) {
	const s = getState()
	if (f.t === "ready") return
	if (f.projectId && f.projectId !== s.projectId) {
		setState(st => ({ pulse: st.pulse + 1 }))
		if (f.t === "message")
			setState(st => ({
				activity: {
					...st.activity,
					[f.projectId!]: (st.activity[f.projectId!] ?? 0) + 1,
				},
			}))
		return
	}
	switch (f.t) {
		case "message":
		case "message_updated": {
			const m = f.message
			setState(st => {
				const messages = { ...st.messages, [m.id]: mergeIncoming(st.messages[m.id], m) }
				// a reply we had not seen: bump the root's count until its own update arrives
				if (f.t === "message" && m.threadId && !st.messages[m.id]) {
					const root = messages[m.threadId]
					if (root)
						messages[m.threadId] = {
							...root,
							replyCount: root.replyCount + 1,
							lastReplyAt: m.createdAt,
						}
				}
				return { messages, ...bumpRoom(st, m) }
			})
			const ui = getState().ui
			if (
				f.t === "message" &&
				ui.roomId === m.roomId &&
				document.visibilityState === "visible"
			)
				markRead(m.roomId)
			return
		}
		case "delivery": {
			const m = s.messages[f.messageId]
			if (!m) return
			const list = [...(m.deliveries ?? [])]
			const i = list.findIndex(d => d.agentId === f.delivery.agentId)
			if (i >= 0) list[i] = f.delivery
			else list.push(f.delivery)
			setState(st => ({
				messages: { ...st.messages, [m.id]: { ...m, deliveries: list } },
			}))
			return
		}
		case "agent":
			upsertAgent(f.agent)
			return
		case "presence": {
			const a = s.agents[f.agentId]
			if (a)
				setState(st => ({
					agents: {
						...st.agents,
						[a.id]: { ...a, state: f.state, lastSeenAt: f.lastSeenAt },
					},
				}))
			return
		}
		case "room": {
			const prev = s.rooms[f.room.id]
			setState(st => ({
				rooms: { ...st.rooms, [f.room.id]: { ...f.room, readSeq: prev?.readSeq ?? 0 } },
			}))
			return
		}
	}
}

// --- panels -----------------------------------------------------------------------------

/** Opens the project's settings, optionally scrolled to one section (`#settings-<section>`). */
export function openSettings(section?: "people" | "invite" | "github") {
	setUi({ settings: true })
	if (!section) return
	let tries = 0
	const go = () => {
		const el = document.getElementById(`settings-${section}`)
		if (!el) return void (++tries < 20 && requestAnimationFrame(go))
		el.scrollIntoView({ block: "start", behavior: "smooth" })
		el.querySelector<HTMLElement>("input:not([readonly]), select, button")?.focus({ preventScroll: true })
	}
	requestAnimationFrame(go)
}

export function toggleLeft() {
	const v = !getState().ui.leftOpen
	setUi({ leftOpen: v })
	savePrefs({ leftOpen: v })
}
export function toggleGithub() {
	const s = getState()
	const v = !s.hideGithub
	setState({ hideGithub: v })
	if (s.projectId) saveHideGithub(s.projectId, v)
}
export function toggleRight() {
	const v = !getState().ui.rightOpen
	setUi({ rightOpen: v })
	savePrefs({ rightOpen: v })
}
